import type { Sessao } from './auth';
import { escreverNaApi as escrever, lerDaApi as ler } from './giro';

/**
 * BLOCO 11 — o front de /api/auditoria (telas 22, 23 e 24).
 *
 * Os tipos espelham `@inovapay/database/giro/auditoria`. Toda escrita tem os TRÊS desfechos do
 * Giro (confirmada, recusada, incerta): incerto nunca aparece como recusa.
 */

export type EstadoDoCaso =
  'NOVA' | 'EM_ANALISE' | 'AGUARDANDO_DADOS' | 'RECONTAGEM' | 'CONCLUIDA' | 'REABERTA';
export type ResultadoDaAuditoria =
  | 'BALCONISTA_CORRETO'
  | 'ERRO_BALCONISTA_CONFIRMADO'
  | 'INCONCLUSIVO'
  | 'PROBLEMA_DADOS'
  | 'PRODUTO_MUDOU_LOCAL';
export type EtiquetaDoCaso =
  'REPOSICAO_INCOMPLETA' | 'ESTOQUE_INCONSISTENTE' | 'NAO_LOCALIZADO' | 'REINCIDENTE' | 'ATRASADA';
export type LocalDaRecontagem = 'PRATELEIRA' | 'OUTRA_EXPOSICAO' | 'ESTOQUE';
export type NivelDeQualidade = 'OK' | 'AVISO' | 'CRITICO' | 'BLOQUEIO';
export type AbaDaFila = 'fila' | 'recontagens' | 'concluidas' | 'historico';

/* Divergências 3.0 — as três etapas (espelham @inovapay/domain/giro/etapas). */
export type EtapaDaDivergencia = 'PRATELEIRA' | 'ESTOQUE' | 'AJUSTE' | 'CONCLUIDA';
/** ANTERIOR = caso de antes das três etapas (segue a recontagem de antes). */
export type FluxoDaDivergencia = 'ANTERIOR' | 'TRES_ETAPAS';
export type ResultadoDaBusca = 'TODAS' | 'PARTE' | 'NENHUMA' | 'NAO_VERIFICOU';
export type SituacaoDaPrateleira =
  | 'NA_PRATELEIRA'
  | 'OUTRA_EXPOSICAO'
  | 'SEM_PRODUTO'
  | 'APRESENTACAO_DIFERENTE'
  | 'LOCAL_INCORRETO'
  | 'CODIGO_INCORRETO';
export type LocalDoEstoque =
  | 'ESTOQUE_SUPERIOR'
  | 'PRATELEIRA_DO_ESTOQUE'
  | 'ARMARIO'
  | 'GAVETA'
  | 'OUTRA_EXPOSICAO'
  | 'LOCAL_CADASTRADO'
  | 'NAO_IDENTIFICADO';
export type DesfechoDoCaso =
  | 'SEM_AJUSTE'
  | 'COM_AJUSTE'
  | 'PROBLEMA_DADOS'
  | 'SEM_EVIDENCIA'
  | 'NOVA_CONTAGEM'
  | 'CORRIGIDO_PELO_BALCONISTA';
export type EstadoDoAjuste = 'PENDENTE_NO_HOS' | 'CONFIRMADO' | 'CANCELADO';

export interface CasoDaFila {
  readonly id: string;
  readonly numero: number;
  readonly produtoId: string;
  readonly produto: string;
  readonly codigo: string | null;
  /** Laboratório/fabricante do cadastro; null = não informado. */
  readonly fabricante: string | null;
  /** Foto do produto (InovaPED, por EAN exato); null = sem foto. */
  readonly fotoProduto: string | null;
  readonly ean: string | null;
  /** Estoque atual do espelho do HOS; null = sem leitura. */
  readonly estoqueAtual: number | null;
  /** O que o balconista contou na prateleira; null = não informou. */
  readonly prateleira: number | null;
  readonly setor: string;
  readonly setorId: string;
  readonly bloco: number;
  readonly faixa: string | null;
  readonly autoriaId: string;
  readonly autoria: string | null;
  readonly solicitado: number;
  readonly encontrado: number;
  /** Negativa: encontrado − solicitado. */
  readonly diferenca: number;
  readonly evidencias: number;
  readonly recontagens: number;
  readonly abertoEm: string;
  readonly slaAte: string;
  readonly atrasada: boolean;
  readonly estado: EstadoDoCaso;
  readonly analisadaPor: string | null;
  readonly analisadaPorMim: boolean;
  /** Resultado VIGENTE. `null` = aberto ou reaberto. */
  readonly resultado: ResultadoDaAuditoria | null;
  readonly rebaixado: boolean;
  readonly concluidaEm: string | null;
  readonly reincidencias: number;
  readonly etiquetas: readonly EtiquetaDoCaso[];
  /** Caso de estoque (prateleira > sistema): o servidor recusa "erro do balconista". */
  readonly deEstoque?: boolean;
  readonly versao: number;
  /** Divergências 3.0: de quem é a vez e em que rodada. */
  readonly etapa: EtapaDaDivergencia;
  readonly rodada: number;
  readonly fluxo?: FluxoDaDivergencia;
  /** Unidades que a busca no estoque precisa localizar (as que faltaram na reposição). */
  readonly alvo: number;
  /** Saldo do HOS na abertura — só contexto. */
  readonly estoqueHosAbertura: number | null;
  /** Resumo da conferência (14/10): hora e fonte do saldo congelado; servidor antigo não manda. */
  readonly estoqueHosAberturaEm?: string | null;
  readonly estoqueHosAberturaFonte?: 'CONTAGEM' | 'GERACAO' | 'SEM_LEITURA' | null;
  /** Quando o espelho do HOS viu o produto pela última vez (a hora do `estoqueAtual`). */
  readonly estoqueAtualEm?: string | null;
  /** Quando o balconista contou a prateleira (a contagem original). */
  readonly contadoEm?: string | null;
  readonly felipeEncontrou: number | null;
  readonly igorLocalizou: number | null;
  readonly igorResultado: ResultadoDaBusca | 'CONTOU' | null;
  readonly ajustePendente: boolean;
  /** Premium (13/10): falta a contagem da prateleira / do estoque nesta rodada. */
  readonly faltaPrateleira?: boolean;
  readonly faltaEstoque?: boolean;
  /** Alguém foi conferir e não conseguiu (rodada atual). */
  readonly naoVerificado?: NaoVerificado | null;
  /** O selo mais importante do caso (servidor). */
  readonly prioridade?: PrioridadeDoCaso;
  /** Os três números (14/10); servidor antigo não manda. */
  readonly numeros?: NumerosDaDivergencia;
  /** BUSCA = HOS acima de 6, procura só as `alvo` que faltaram; TOTAL = conta o estoque inteiro. */
  readonly modoEstoque?: 'BUSCA' | 'TOTAL';
}

/**
 * OS TRÊS NÚMEROS (14/10): falta na reposição (a tarefa) · ainda não comprovado (pendência de
 * busca, nunca perda) · divergência real (só depois das contagens e da conciliação).
 */
export interface NumerosDaDivergencia {
  readonly metodo: 'ATUAL' | 'ANTERIOR';
  readonly tipo: 'FALTOU_NA_REPOSICAO' | 'ESTOQUE_INCONSISTENTE';
  readonly situacao:
    | 'REPOSICAO_INCOMPLETA'
    | 'REPOSICAO_ATENDIDA'
    | 'PRATELEIRA_ACIMA_DO_HOS'
    | 'SEM_LEITURA_DO_HOS';
  readonly hosNaContagem: number | null;
  readonly prateleira: number | null;
  readonly esperadoNoEstoque: number | null;
  readonly acimaDoHos: number;
  readonly solicitado: number;
  readonly localizado: number | null;
  readonly reposto: number | null;
  readonly faltaNaReposicao: number;
  readonly naoComprovado: number | null;
  readonly localizouAlemDoEsperado: boolean;
}
export interface ExplicacaoDoCaso {
  readonly linhas: readonly string[];
  readonly conclusao: string | null;
  readonly confirmada: boolean;
}

export type LocalDaConferencia = 'PRATELEIRA' | 'ESTOQUE';
export type MotivoSemVerificacao =
  'PRODUTO_NAO_LOCALIZADO' | 'LOCAL_INACESSIVEL' | 'SEM_IDENTIFICACAO' | 'OUTRO';
export interface NaoVerificado {
  readonly local: LocalDaConferencia;
  readonly motivo: MotivoSemVerificacao;
  readonly observacao: string | null;
  readonly por: string | null;
  readonly em: string;
}
export type SeloDoCaso =
  | 'URGENTE'
  | 'REINCIDENTE'
  | 'AGUARDANDO_PRATELEIRA'
  | 'AGUARDANDO_ESTOQUE'
  | 'PRONTO_PARA_DECISAO'
  | 'NAO_VERIFICADO'
  | 'CONCLUIDO';
export interface PrioridadeDoCaso {
  readonly nivel: 0 | 1 | 2 | 3;
  readonly selo: SeloDoCaso;
  readonly motivo: string | null;
}

export interface FilaDoAuditor {
  readonly atualizadoEm: string;
  readonly hoje: string;
  readonly prioridadePor: 'DIFERENCA' | 'IDADE' | 'VALOR';
  readonly indicadores: {
    readonly novas: number;
    readonly reabertas: number;
    readonly emAnalise: number;
    readonly emAnalisePorMim: number;
    readonly aguardandoDados: number;
    readonly recontagens: number;
    readonly concluidasHoje: number;
    readonly concluidasHojeComErro: number;
    readonly atrasadas: number;
    readonly reincidentes: number;
    /** Casos abertos em cada etapa, no mesmo escopo de filtros. */
    readonly aguardandoPrateleira: number;
    readonly aguardandoEstoque: number;
    readonly aguardandoAjuste: number;
    /** Abertos de antes das três etapas. */
    readonly anteriores?: number;
    /** Premium: casos DISTINTOS que esperam alguma contagem. */
    readonly aguardandoConferencia?: number;
    readonly naoVerificados?: number;
  };
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly casos: readonly CasoDaFila[];
  readonly setores: readonly { readonly id: string; readonly nome: string }[];
  readonly pessoas: readonly { readonly id: string; readonly nome: string }[];
  /** Os grupos reais do cadastro do HOS com divergência; servidor antigo não manda. */
  readonly grupos?: readonly string[];
}

export interface FiltrosDaFila {
  readonly aba?: AbaDaFila;
  readonly estados?: readonly EstadoDoCaso[];
  readonly resultados?: readonly ResultadoDaAuditoria[];
  readonly etiquetas?: readonly EtiquetaDoCaso[];
  readonly setor?: string | null;
  /** Grupo do cadastro do HOS (GENERICOS, SIMILARES…). */
  readonly grupo?: string | null;
  readonly pessoa?: string | null;
  readonly busca?: string | null;
  /** Janela dos concluídos (caso aberto aparece sempre). */
  readonly dias?: number;
  /** Período REAL (dia da divergência), AAAA-MM-DD. */
  readonly de?: string | null;
  readonly ate?: string | null;
  /** Com período: traz também os abertos antigos. */
  readonly abertos?: boolean;
  readonly difMin?: number | null;
  readonly difMax?: number | null;
  readonly ordem?: OrdemDaFila | null;
  readonly limite?: number;
  /** Divergências 3.0: só os casos desta etapa. */
  readonly etapa?: EtapaDaDivergencia | null;
  /** 'ANTERIOR' = os abertos de antes das três etapas (aba Anteriores). */
  readonly fluxo?: FluxoDaDivergencia | null;
  /** Página (1, 2, …) de `limite` casos. */
  readonly pagina?: number;
  /** Premium: a fila de quem confere este local (sem os "não consegui" vigentes). */
  readonly conferir?: LocalDaConferencia | null;
  /** Premium: só abertos (aba "Todas"). */
  readonly somenteAbertos?: boolean;
}
export type OrdemDaFila = 'PRIORIDADE' | 'DIFERENCA' | 'ANTIGA' | 'ROTA' | 'CATEGORIA' | 'AZ';

export interface MovimentoNoPeriodo {
  readonly tipo: 'VENDA' | 'REPOSICAO' | 'COBERTURA_INCOMPLETA';
  readonly qtd: number | null;
  readonly texto: string;
}
export interface MovimentosDoCaso {
  readonly desde: string;
  readonly ate: string;
  readonly movimentos: readonly MovimentoNoPeriodo[];
  readonly vendasCobertasAte: string | null;
  readonly coberturaCompleta: boolean;
  readonly vendas: readonly {
    readonly em: string;
    readonly qtd: number;
    readonly cupom: string | null;
  }[];
  readonly reposicoes: readonly {
    readonly em: string;
    readonly qtd: number;
    readonly por: string | null;
  }[];
}
export interface EventoDoCaso {
  readonly em: string;
  readonly origem: 'humana' | 'sistema' | 'externa';
  readonly texto: string;
  readonly detalhe: string | null;
  readonly quem: string | null;
}
export interface RecontagemDoCaso {
  readonly id: string;
  readonly por: string | null;
  readonly encontrado: number;
  readonly local: LocalDaRecontagem;
  readonly observacao: string | null;
  readonly fotoId: string | null;
  readonly em: string;
}
export interface AuditoriaDoCaso {
  readonly id: string;
  readonly por: string | null;
  readonly resultado: ResultadoDaAuditoria;
  readonly escolhido: ResultadoDaAuditoria | null;
  readonly conclusiva: boolean;
  readonly encontrado: number | null;
  readonly observacao: string | null;
  readonly rebaixado: string | null;
  /** Motivo da reabertura (linha de reabertura). */
  readonly reabertura: string | null;
  readonly em: string;
}
export interface NotaDoCaso {
  readonly tipo: 'OBSERVACAO' | 'ENCAMINHAMENTO' | 'RETOMADA';
  readonly destino: 'ADMIN' | 'DADOS' | 'RECONTAGEM' | null;
  readonly texto: string;
  readonly por: string | null;
  readonly em: string;
}
export interface Sugestao {
  readonly sugestao: ResultadoDaAuditoria;
  readonly conclusivo: boolean;
  readonly porque: string;
}
export interface CasoCompleto extends CasoDaFila {
  readonly ean: string | null;
  readonly dia: string;
  readonly grandezas: {
    readonly prateleira: number | null;
    readonly alvo: number;
    readonly estoqueUsado: number | null;
    readonly estoqueUsadoFonte: string;
    readonly estoqueUsadoEm: string | null;
    readonly solicitado: number;
    readonly encontrado: number;
    readonly reposto: number | null;
    readonly diferenca: number;
    readonly contadoEm: string | null;
    readonly repostoEm: string | null;
    readonly entrada: 'SCAN' | 'MANUAL' | null;
  };
  readonly contadoPor: string | null;
  readonly linhaDoTempo: readonly EventoDoCaso[];
  readonly movimentos: MovimentosDoCaso;
  readonly fotos: readonly {
    readonly id: string;
    readonly tipo: 'contagem' | 'recontagem';
    readonly por: string | null;
    readonly em: string;
  }[];
  readonly recontagensFeitas: readonly RecontagemDoCaso[];
  readonly auditorias: readonly AuditoriaDoCaso[];
  readonly notas: readonly NotaDoCaso[];
  readonly sugestao: Sugestao | null;
  readonly config: { readonly recontagemExigeFoto: boolean; readonly reaberturaPermitida: boolean };
}

export interface CasoDaConta {
  readonly divergenciaId: string;
  readonly auditoriaId: string;
  readonly numero: number;
  readonly produto: string;
  readonly setor: string;
  readonly bloco: number;
  readonly resultado: ResultadoDaAuditoria;
  readonly abertoEm: string;
  readonly concluidoEm: string;
  readonly motivoExclusao: string | null;
}
export interface ContaDaPessoa {
  readonly accountId: string;
  readonly nome: string | null;
  readonly taxa: number;
  readonly nivel: NivelDeQualidade;
  readonly bloqueia: boolean;
  readonly erros: number;
  readonly conclusivas: number;
  readonly amostraOk: boolean;
  readonly minimo: number;
  readonly threshold: number;
  readonly janelaTipo: 'DIAS' | 'ULTIMAS_N';
  readonly janelaValor: number;
  readonly porque: string;
  readonly janelaDesde: string | null;
  readonly janelaAte: string;
  readonly novaJanelaDesde: string | null;
  readonly incluidos: readonly CasoDaConta[];
  readonly excluidos: readonly CasoDaConta[];
  readonly bloqueio: {
    readonly id: string;
    readonly desde: string;
    readonly origem: 'AUTOMATICO' | 'MANUAL';
    readonly taxa: number;
  } | null;
}

export interface ConfigDaAuditoria {
  readonly prioridade_por: 'DIFERENCA' | 'IDADE' | 'VALOR';
  readonly reincidencia_dias: number;
  readonly recontagem_exige_foto: boolean;
  readonly reabertura_permitida: boolean;
  /** Retém a premiação de quem é bloqueado. Nasce DESLIGADO: decisão do dono. */
  readonly bloqueio_ativo: boolean;
  readonly bloqueio_automatico: boolean;
  readonly aviso_pct: number;
  readonly critico_pct: number;
  readonly bloqueio_pct: number;
  readonly minimo_amostra: number;
  readonly janela_tipo: 'DIAS' | 'ULTIMAS_N';
  readonly janela_valor: number;
  readonly release_nova_janela: boolean;
}
export type ConfigVigente = ConfigDaAuditoria & {
  readonly versao: number;
  readonly desde: string | null;
};

export interface PessoaNaQualidade {
  readonly accountId: string;
  readonly nome: string | null;
  readonly setores: readonly string[];
  readonly blocos: number;
  readonly skus: number;
  readonly divergencias: number;
  readonly conclusivas: number;
  readonly erros: number;
  /** `null` = sem conclusiva (nunca "0%"). */
  readonly taxa: number | null;
  readonly nivel: NivelDeQualidade;
  readonly amostraOk: boolean;
  readonly bloqueada: boolean;
  readonly porque: string;
}
export interface QualidadeDoGiro {
  readonly dias: number;
  readonly desde: string;
  readonly ate: string;
  readonly terminaram: {
    readonly casos: number;
    readonly concluidos: number;
    readonly emAberto: number;
    readonly porResultado: Readonly<Record<ResultadoDaAuditoria, number>>;
    readonly elegiveis: number;
  };
  readonly dados: readonly {
    readonly chave: string;
    readonly rotulo: string;
    readonly valor: number | null;
    readonly semFonte?: string;
  }[];
  readonly pessoas: readonly PessoaNaQualidade[];
  readonly bloqueiosAtivos: number;
  readonly config: ConfigVigente;
}

export interface LimitesDoBloqueio {
  readonly aviso: number;
  readonly critico: number;
  readonly bloqueio: number;
  readonly minimo: number;
  readonly janelaTipo: 'DIAS' | 'ULTIMAS_N';
  readonly janelaValor: number;
}
export interface BloqueioDoGiro {
  readonly id: string;
  readonly accountId: string;
  readonly pessoa: string | null;
  readonly estado: 'ATIVO' | 'LIBERADO';
  readonly origem: 'AUTOMATICO' | 'MANUAL';
  readonly escopo: 'PREMIACAO';
  readonly erros: number;
  readonly conclusivas: number;
  readonly taxa: number;
  readonly limite: number;
  readonly minimo: number;
  readonly janelaTipo: 'DIAS' | 'ULTIMAS_N';
  readonly janelaValor: number;
  readonly janelaDesde: string | null;
  readonly janelaAte: string;
  readonly limites: LimitesDoBloqueio;
  readonly porque: string;
  readonly motivo: string | null;
  readonly criadoEm: string;
  readonly criadoPor: string | null;
  readonly liberadoEm: string | null;
  readonly liberadoPor: string | null;
  readonly liberadoMotivo: string | null;
  readonly novaJanela: boolean;
  readonly versao: number;
}
export interface DetalheDoBloqueio extends BloqueioDoGiro {
  readonly incluidos: readonly CasoDaConta[];
  readonly excluidos: readonly CasoDaConta[];
}
export interface PerfilDeQualidade {
  readonly accountId: string;
  readonly nome: string | null;
  readonly dias: number;
  readonly setores: readonly string[];
  readonly operacao: {
    readonly blocos: number;
    readonly skus: number;
    readonly unidadesRepostas: number;
  };
  readonly qualidade: {
    readonly divergencias: number;
    readonly conclusivas: number;
    readonly corretos: number;
    readonly erros: number;
    readonly inconclusivos: number;
    readonly dados: number;
    readonly mudouLocal: number;
    readonly taxa: number | null;
  };
  readonly metodos: {
    readonly escaneado: number;
    readonly manual: number;
    readonly fotosExigidas: number;
    readonly fotosEntregues: number;
    readonly recontagensRecebidas: number;
  };
  readonly conta: ContaDaPessoa;
}
export interface SimulacaoDeLimites {
  readonly aviso: number;
  readonly critico: number;
  readonly bloqueio: number;
  readonly insuficiente: number;
  readonly pessoas: number;
}
export interface ResultadoDaConclusao {
  readonly resultado: ResultadoDaAuditoria;
  readonly escolhido: ResultadoDaAuditoria;
  readonly conclusiva: boolean;
  readonly rebaixado: string | null;
  readonly movimentos: readonly MovimentoNoPeriodo[];
  readonly bloqueioAplicado: boolean;
}

/* ------------------------------------------------------------------ *
 * Chamadas
 * ------------------------------------------------------------------ */

const consulta = (f: FiltrosDaFila) => {
  const p = new URLSearchParams();
  if (f.aba) p.set('aba', f.aba);
  if (f.estados?.length) p.set('estados', f.estados.join(','));
  if (f.resultados?.length) p.set('resultados', f.resultados.join(','));
  if (f.etiquetas?.length) p.set('etiquetas', f.etiquetas.join(','));
  if (f.setor) p.set('setor', f.setor);
  if (f.grupo) p.set('grupo', f.grupo);
  if (f.pessoa) p.set('pessoa', f.pessoa);
  if (f.busca?.trim()) p.set('busca', f.busca.trim());
  if (f.dias) p.set('dias', String(f.dias));
  if (f.de) p.set('de', f.de);
  if (f.ate) p.set('ate', f.ate);
  if (f.abertos) p.set('abertos', '1');
  if (f.difMin != null) p.set('difMin', String(f.difMin));
  if (f.difMax != null) p.set('difMax', String(f.difMax));
  if (f.ordem && f.ordem !== 'PRIORIDADE') p.set('ordem', f.ordem);
  if (f.limite) p.set('limite', String(f.limite));
  if (f.etapa) p.set('etapa', f.etapa);
  if (f.fluxo) p.set('fluxo', f.fluxo);
  if (f.pagina && f.pagina > 1) p.set('pagina', String(f.pagina));
  if (f.conferir) p.set('conferir', f.conferir);
  if (f.somenteAbertos) p.set('somenteAbertos', '1');
  return p.toString();
};

export const lerFila = (f: FiltrosDaFila = {}) =>
  ler<FilaDoAuditor>(`/api/auditoria/fila?${consulta(f)}`);
export const urlDoRelatorio = (f: FiltrosDaFila = {}) =>
  `/api/auditoria/relatorio.csv?${consulta(f)}`;
export const lerCaso = (id: string) => ler<CasoCompleto>(`/api/auditoria/casos/${id}`);
export const iniciarAnalise = (s: Sessao, id: string, versao?: number) =>
  escrever<{ estado: EstadoDoCaso; versao: number }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/analise`,
    {
      ...(versao === undefined ? {} : { versao }),
    },
  );
export const registrarRecontagem = (
  s: Sessao,
  id: string,
  d: {
    encontrado: number;
    local: LocalDaRecontagem;
    observacao?: string | null;
    evidenciaId?: string | null;
  },
) =>
  escrever<{ recontagemId: string; versao: number; sugestao: Sugestao }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/recontagem`,
    d,
  );
export const anotar = (
  s: Sessao,
  id: string,
  d: { texto: string; encaminharPara?: 'ADMIN' | 'DADOS' | 'RECONTAGEM' | null; versao?: number },
) =>
  escrever<{ estado: EstadoDoCaso; versao: number }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/notas`,
    d,
  );
export const concluir = (
  s: Sessao,
  id: string,
  d: {
    resultado: ResultadoDaAuditoria;
    observacao: string;
    versao: number;
    desfecho?: DesfechoDoCaso;
    /** Concluir antes da etapa de ajuste (exige motivo). */
    pularEtapas?: boolean;
    /** Premium: a causa e, separada, a resposta "foi erro do balconista?". */
    causa?: CausaDaDivergencia;
    erroBalconista?: ErroDoBalconista;
    /** Política de qualidade (14/10): as duas classificações (o servidor deriva a antiga). */
    erroContagem?: ErroDoBalconista;
    falhaReposicao?: ErroDoBalconista | null;
  },
) => escrever<ResultadoDaConclusao>(s, 'POST', `/api/auditoria/casos/${id}/concluir`, d);
export const reabrir = (s: Sessao, id: string, d: { motivo: string; versao: number }) =>
  escrever<{ estado: EstadoDoCaso; versao: number }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/reabrir`,
    d,
  );

/* ------------------------------------------------------------------ *
 * Divergências 3.0 — as três etapas
 * ------------------------------------------------------------------ */

export interface SugestaoDoAjuste {
  readonly resultado: ResultadoDaAuditoria;
  readonly texto: string;
}
export interface LocalComQuantidade {
  readonly local: LocalDoEstoque;
  readonly qtd: number;
}
export interface EtapasDoCaso {
  /** Fluxo de três etapas ligado? Desligado = a tela antiga. */
  readonly ativo: boolean;
  /** ANTERIOR = caso de antes das três etapas: a tela de antes, mesmo com o fluxo ligado. */
  readonly fluxo?: FluxoDaDivergencia;
  readonly etapa: EtapaDaDivergencia;
  readonly rodada: number;
  readonly alvo: number;
  readonly abertura: {
    readonly produto: string | null;
    readonly codigo: string | null;
    readonly ean: string | null;
    readonly setor: string | null;
    readonly prateleira: number | null;
    readonly pedido: number;
    readonly achou: number;
    readonly reposto: number | null;
    readonly naoLocalizado: number;
    readonly estoqueHos: number | null;
    readonly estoqueEm: string | null;
    readonly estoqueFonte: 'CONTAGEM' | 'GERACAO' | 'SEM_LEITURA' | null;
    readonly reconstruida: boolean;
    /** O número do motor — não é divergência física comprovada. */
    readonly divergenciaReportada: number;
  };
  /** Saldo do HOS agora (cadastro de hoje), ao lado do da contagem. */
  readonly agora?: { readonly estoqueHos: number | null; readonly em: string | null };
  readonly prateleira: readonly {
    readonly rodada: number;
    readonly pessoa: string | null;
    readonly encontrado: number;
    readonly situacao: SituacaoDaPrateleira;
    readonly observacao: string | null;
    readonly liberouPara: string | null;
    readonly temFoto: boolean;
    readonly em: string;
  }[];
  readonly estoque: readonly {
    readonly rodada: number;
    readonly pessoa: string | null;
    readonly alvo: number;
    readonly localizado: number;
    /** CONTOU = contagem do estoque inteiro (modo TOTAL). */
    readonly resultado: ResultadoDaBusca | 'CONTOU';
    readonly locais: readonly LocalComQuantidade[];
    readonly observacao: string | null;
    readonly temFoto: boolean;
    readonly em: string;
    /** BUSCA = procurou só as que faltaram (até 13/10); TOTAL = contou tudo o que tinha lá embaixo. */
    readonly modo: 'BUSCA' | 'TOTAL';
    /** modo TOTAL: unidades no estoque; null = não conseguiu verificar. */
    readonly total: number | null;
  }[];
  readonly ajustes: readonly {
    readonly id: string;
    readonly pessoa: string | null;
    readonly estado: EstadoDoAjuste;
    readonly fisicoTotal: number;
    readonly saldoHos: number;
    readonly ajuste: number;
    readonly motivo: string;
    readonly referencia: string | null;
    readonly em: string;
    readonly decididoEm: string | null;
  }[];
  readonly sugestao: SugestaoDoAjuste;
}

export const lerEtapas = (id: string) => ler<EtapasDoCaso>(`/api/auditoria/casos/${id}/etapas`);
export const conferirPrateleira = (
  s: Sessao,
  id: string,
  d: {
    versao: number;
    /** UUID de UMA ação do usuário: o mesmo em cada nova tentativa do mesmo clique. */
    chave: string;
    encontrado: number;
    situacao: SituacaoDaPrateleira;
    observacao?: string | null;
    evidenciaId?: string | null;
    liberar: boolean;
  },
) =>
  escrever<{ etapa: EtapaDaDivergencia; versao: number; repetido: boolean }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/prateleira`,
    d,
  );
export const conferirEstoque = (
  s: Sessao,
  id: string,
  d: {
    versao: number;
    chave: string;
    localizado: number;
    resultado: ResultadoDaBusca;
    locais: readonly LocalComQuantidade[];
    observacao?: string | null;
    evidenciaId?: string | null;
  },
) =>
  escrever<{ etapa: EtapaDaDivergencia; versao: number; repetido: boolean }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/estoque`,
    d,
  );
/** Etapa 2 simples (dono, 13/10): quanto tem no estoque inteiro; null = não consegui verificar. */
export const contarEstoque = (
  s: Sessao,
  id: string,
  d: {
    chave: string;
    total?: number | null;
    /** Busca (HOS acima de 6): das que faltaram, quantas achou. */
    localizado?: number;
    /** Botão "Tem mais de 12". */
    maisDe12?: boolean;
    observacao?: string | null;
  },
) =>
  escrever<{ etapa: EtapaDaDivergencia; versao: number; repetido: boolean }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/estoque-total`,
    d,
  );
export const pedirNovaConferencia = (
  s: Sessao,
  id: string,
  d: { versao: number; etapa: 'PRATELEIRA' | 'ESTOQUE'; motivo: string },
) =>
  escrever<{ etapa: EtapaDaDivergencia; versao: number }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/nova-conferencia`,
    d,
  );
export const registrarAjusteNoHos = (
  s: Sessao,
  id: string,
  d: { versao: number; chave: string; fisicoTotal: number; motivo: string },
) =>
  escrever<{ ajusteId: string; ajuste: number; saldoHos: number; repetido: boolean }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/ajustes`,
    d,
  );
export const decidirAjuste = (
  s: Sessao,
  ajusteId: string,
  d: { decisao: 'CONFIRMADO' | 'CANCELADO'; referencia?: string | null },
) => escrever<unknown>(s, 'POST', `/api/auditoria/ajustes/${ajusteId}/decisao`, d);

export const lerQualidade = (dias = 30) =>
  ler<QualidadeDoGiro>(`/api/auditoria/qualidade?dias=${String(dias)}`);
export const lerPerfilDeQualidade = (accountId: string, dias = 30) =>
  ler<PerfilDeQualidade>(`/api/auditoria/pessoas/${accountId}?dias=${String(dias)}`);
export const lerBloqueios = () =>
  ler<{ bloqueios: readonly BloqueioDoGiro[] }>('/api/auditoria/bloqueios');
export const lerBloqueio = (id: string) => ler<DetalheDoBloqueio>(`/api/auditoria/bloqueios/${id}`);
export const aplicarBloqueio = (s: Sessao, accountId: string, motivo: string) =>
  escrever<{ id: string }>(s, 'POST', '/api/auditoria/bloqueios', { accountId, motivo });
export const liberarBloqueio = (
  s: Sessao,
  id: string,
  d: { motivo: string; novaJanela: boolean; versao: number },
) => escrever<{ estado: 'LIBERADO' }>(s, 'POST', `/api/auditoria/bloqueios/${id}/liberar`, d);
export const lerConfigDaAuditoria = () => ler<ConfigVigente>('/api/auditoria/config');
export const salvarConfigDaAuditoria = (s: Sessao, valor: ConfigDaAuditoria, motivo: string) =>
  escrever<{ versao: number }>(s, 'PUT', '/api/auditoria/config', { valor, motivo });
export const simularLimites = (s: Sessao, limites: LimitesDoBloqueio) =>
  escrever<SimulacaoDeLimites>(s, 'POST', '/api/auditoria/simular', limites);
export const lerMinhasAuditorias = (dias = 30) =>
  ler<{ casos: readonly CasoDaFila[]; conta: ContaDaPessoa; retencaoLigada: boolean }>(
    `/api/auditoria/minhas?dias=${String(dias)}`,
  );

/* ------------------------------------------------------------------ *
 * Rótulos — linguagem factual, nunca julgamento sobre a pessoa
 * ------------------------------------------------------------------ */

export const ROTULO_DO_ESTADO: Readonly<Record<EstadoDoCaso, string>> = {
  NOVA: 'Nova',
  EM_ANALISE: 'Em análise',
  AGUARDANDO_DADOS: 'Aguardando dados',
  RECONTAGEM: 'Recontagem pendente',
  CONCLUIDA: 'Concluída',
  REABERTA: 'Reaberta',
};
export const ROTULO_DO_RESULTADO: Readonly<Record<ResultadoDaAuditoria, string>> = {
  BALCONISTA_CORRETO: 'Balconista correto',
  ERRO_BALCONISTA_CONFIRMADO: 'Erro confirmado',
  INCONCLUSIVO: 'Inconclusivo',
  PROBLEMA_DADOS: 'Problema de dados',
  PRODUTO_MUDOU_LOCAL: 'Produto mudou de local',
};
export const EXPLICACAO_DO_RESULTADO: Readonly<Record<ResultadoDaAuditoria, string>> = {
  BALCONISTA_CORRETO: 'A evidência não atribui a diferença à pessoa',
  ERRO_BALCONISTA_CONFIRMADO: 'Exige evidência, recontagem e motivo',
  INCONCLUSIVO: 'Não entra como erro confirmado em nenhuma métrica',
  PROBLEMA_DADOS: 'Saldo antigo, integração fora, cadastro inconsistente',
  PRODUTO_MUDOU_LOCAL: 'Permite sugerir atualização de localização ao Admin',
};
export const ROTULO_DA_ETIQUETA: Readonly<Record<EtiquetaDoCaso, string>> = {
  REPOSICAO_INCOMPLETA: 'Reposição incompleta',
  ESTOQUE_INCONSISTENTE: 'Estoque inconsistente',
  NAO_LOCALIZADO: 'Não localizado',
  REINCIDENTE: 'Reincidente',
  ATRASADA: 'Prazo vencido',
};
export const ROTULO_DO_LOCAL: Readonly<Record<LocalDaRecontagem, string>> = {
  PRATELEIRA: 'Prateleira do bloco',
  OUTRA_EXPOSICAO: 'Outra exposição',
  ESTOQUE: 'Estoque',
};
export const ROTULO_DO_NIVEL: Readonly<Record<NivelDeQualidade, string>> = {
  OK: 'Dentro do padrão',
  AVISO: 'Aviso',
  CRITICO: 'Crítico',
  BLOQUEIO: 'Acima do limite',
};

export const ROTULO_DA_ETAPA: Readonly<Record<EtapaDaDivergencia, string>> = {
  PRATELEIRA: 'Prateleira',
  ESTOQUE: 'Estoque',
  AJUSTE: 'Ajustes',
  CONCLUIDA: 'Concluída',
};
export const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDaPrateleira, string>> = {
  NA_PRATELEIRA: 'Na prateleira',
  OUTRA_EXPOSICAO: 'Em outra exposição',
  SEM_PRODUTO: 'Sem produto',
  APRESENTACAO_DIFERENTE: 'Apresentação diferente',
  LOCAL_INCORRETO: 'Local incorreto',
  CODIGO_INCORRETO: 'Código incorreto',
};
export const ROTULO_DO_LOCAL_ESTOQUE: Readonly<Record<LocalDoEstoque, string>> = {
  ESTOQUE_SUPERIOR: 'Estoque superior',
  PRATELEIRA_DO_ESTOQUE: 'Prateleira do estoque',
  ARMARIO: 'Armário',
  GAVETA: 'Gaveta',
  OUTRA_EXPOSICAO: 'Outra exposição',
  LOCAL_CADASTRADO: 'Local cadastrado',
  NAO_IDENTIFICADO: 'Não identificado',
};
export const ROTULO_DA_BUSCA: Readonly<Record<ResultadoDaBusca, string>> = {
  TODAS: 'Encontrou todas',
  PARTE: 'Encontrou parte',
  NENHUMA: 'Não encontrou',
  NAO_VERIFICOU: 'Não conseguiu verificar',
};
export const ROTULO_DO_DESFECHO: Readonly<Record<DesfechoDoCaso, string>> = {
  SEM_AJUSTE: 'Sem ajuste no HOS',
  COM_AJUSTE: 'Com ajuste no HOS',
  PROBLEMA_DADOS: 'Problema de dados / cadastro',
  SEM_EVIDENCIA: 'Sem evidência suficiente',
  NOVA_CONTAGEM: 'Pede nova contagem',
  CORRIGIDO_PELO_BALCONISTA: 'Corrigido pelo balconista antes de finalizar',
};
export const ROTULO_DO_AJUSTE: Readonly<Record<EstadoDoAjuste, string>> = {
  PENDENTE_NO_HOS: 'Ajuste pendente no HOS',
  CONFIRMADO: 'Ajuste confirmado',
  CANCELADO: 'Ajuste cancelado',
};

/** "#0281". */
export const numeroDoCaso = (n: number) => `#${String(n).padStart(4, '0')}`;
/** "23 min", "1 h 12", "ontem", "22/09". */
export function ha(iso: string, agora = Date.now()): string {
  const min = Math.max(0, Math.round((agora - Date.parse(iso)) / 60_000));
  if (min < 60) return `${String(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${String(h)} h ${String(min % 60).padStart(2, '0')}`;
  if (h < 48) return 'ontem';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}
export const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
export const dataCurta = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
/** "37,5%" — taxa sempre com uma casa, vírgula. */
export const taxaTexto = (t: number) => `${t.toFixed(1).replace('.', ',')}%`;

/* ------------------------------------------------------------------ *
 * Divergências 3.0 PREMIUM (dono, 13/10/2026)
 * ------------------------------------------------------------------ */

export type CausaDaDivergencia =
  | 'ERRO_DE_CONTAGEM'
  | 'REPOSICAO_INCOMPLETA'
  | 'MOVIMENTACAO_EXPLICA'
  | 'DIVERGENCIA_REAL'
  | 'PROBLEMA_INTEGRACAO'
  | 'PRODUTO_NAO_LOCALIZADO'
  | 'OUTRO';
export type ErroDoBalconista = 'SIM' | 'NAO' | 'INCERTO';

export const ROTULO_DA_CAUSA: Readonly<Record<CausaDaDivergencia, string>> = {
  ERRO_DE_CONTAGEM: 'Erro de contagem',
  REPOSICAO_INCOMPLETA: 'Reposição incompleta',
  MOVIMENTACAO_EXPLICA: 'Movimentação explica a diferença',
  DIVERGENCIA_REAL: 'Divergência real de estoque',
  PROBLEMA_INTEGRACAO: 'Problema de integração HOS',
  PRODUTO_NAO_LOCALIZADO: 'Produto não localizado',
  OUTRO: 'Outro',
};
export const ROTULO_SEM_VERIFICACAO: Readonly<Record<MotivoSemVerificacao, string>> = {
  PRODUTO_NAO_LOCALIZADO: 'Produto não localizado',
  LOCAL_INACESSIVEL: 'Local inacessível',
  SEM_IDENTIFICACAO: 'Produto sem identificação',
  OUTRO: 'Outro motivo',
};

export type TipoDoMovimento =
  | 'VENDA'
  | 'DEVOLUCAO'
  | 'ENTRADA_NF'
  | 'AJUSTE'
  | 'CANCELAMENTO'
  | 'OUTRO'
  | 'REPOSICAO_INTERNA'
  | 'NOTA_EM_CONFERENCIA';
export interface MovimentoDoExtrato {
  readonly chave: string;
  readonly em: string | null;
  readonly dia?: string | null;
  readonly tipo: TipoDoMovimento;
  readonly qtd: number;
  readonly conta: boolean;
  readonly documento: string | null;
  readonly origem: 'HOS_RAZAO' | 'HOS_VENDAS' | 'HOS_NOTAS' | 'HOS_CANCELAMENTOS' | 'GIRO';
  readonly detalhe: string | null;
  readonly cancelado: boolean;
}
export type ConfiancaDaConciliacao = 'CONFIAVEL' | 'APROXIMADA' | 'INCONCLUSIVA' | 'AGUARDANDO';
export interface Conciliacao {
  readonly confianca: ConfiancaDaConciliacao;
  readonly referencia: string | null;
  readonly fisico: { readonly min: number; readonly max: number } | null;
  readonly teorico: number | null;
  readonly teoricoFonte: 'LEITURA_NA_HORA' | 'RECONSTRUIDO' | null;
  readonly diferenca: { readonly min: number; readonly max: number } | null;
  readonly frase: string;
  readonly ressalvas: readonly string[];
}
export interface Investigacao {
  readonly caso: { readonly id: string; readonly numero: number; readonly rodada: number };
  readonly frase: string;
  readonly extrato: {
    readonly fonte: 'HOS_RAZAO' | 'INOVAPAY_VENDAS';
    readonly aviso: string | null;
    readonly desde: string;
    readonly saldoInicial: number | null;
    readonly saldoInicialFonte: 'RAZAO' | 'ABERTURA' | null;
    readonly movimentos: readonly MovimentoDoExtrato[];
    readonly resumo: {
      readonly entradas: number;
      readonly saidas: number;
      readonly saldoTeorico: number | null;
      readonly semHora: number;
    };
    readonly explicacao: string;
    readonly saldoAgora: { readonly saldo: number; readonly em: string } | null;
    readonly notasEmConferencia: number;
  };
  readonly contagens: {
    readonly prateleira: {
      readonly qtd: number;
      readonly em: string;
      readonly por: string | null;
      readonly situacao: SituacaoDaPrateleira;
    } | null;
    readonly estoque: {
      readonly qtd: number;
      readonly em: string;
      readonly por: string | null;
    } | null;
    readonly naoVerificados: readonly NaoVerificado[];
    /** Busca pontual de reposição no estoque (não é o estoque inteiro); servidor antigo não manda. */
    readonly buscaNoEstoque?: {
      readonly qtd: number;
      readonly alvo: number;
      readonly por: string | null;
      readonly em: string;
    } | null;
    /** "Tem mais de 12" no estoque (sem contar). */
    readonly estoqueMaisDe?: {
      readonly piso: number;
      readonly por: string | null;
      readonly em: string;
    } | null;
  };
  readonly leituras: readonly {
    readonly saldo: number;
    readonly em: string;
    readonly origem: 'PRATELEIRA' | 'ESTOQUE' | 'MANUAL';
  }[];
  readonly conciliacao: Conciliacao;
  /** 14/10; servidor antigo não manda. */
  readonly numeros?: NumerosDaDivergencia;
  readonly explicacao?: ExplicacaoDoCaso;
  /** Política de qualidade: o que a evidência permite marcar em cada indicador. */
  readonly pareceres?: {
    readonly erroContagem: ParecerDaEvidencia;
    readonly falhaReposicao: ParecerDaEvidencia;
  };
}
export interface ParecerDaEvidencia {
  readonly permitido: boolean;
  readonly exigeJustificativa: boolean;
  readonly motivo: string | null;
}
export interface Responsaveis {
  readonly prateleira: readonly { readonly id: string; readonly nome: string }[];
  readonly estoque: readonly { readonly id: string; readonly nome: string }[];
  readonly versao: number;
  readonly candidatos: readonly {
    readonly id: string;
    readonly nome: string;
    readonly prateleira: boolean;
    readonly estoque: boolean;
  }[];
  readonly minhaFuncao: { readonly prateleira: boolean; readonly estoque: boolean };
}
export interface PainelDasDivergencias {
  readonly dias: number;
  readonly pendentes: {
    readonly prateleira: number;
    readonly estoque: number;
    readonly decisao: number;
    readonly naoVerificados: number;
    readonly ajustesNoHos: number;
  };
  readonly tempoAteConferir: {
    readonly prateleira: number | null;
    readonly estoque: number | null;
  };
  readonly concluidosPorDia: readonly { readonly dia: string; readonly total: number }[];
  readonly produtosRecorrentes: readonly {
    readonly produtoId: string;
    readonly produto: string;
    readonly casos: number;
  }[];
  readonly porBalconista: readonly {
    readonly accountId: string;
    readonly nome: string;
    readonly casos: number;
    readonly concluidos: number;
    readonly erros: number;
  }[];
  readonly causas: readonly { readonly causa: string; readonly total: number }[];
  readonly reabertos: number;
  readonly inconclusivos: number;
  readonly qualidade: {
    readonly conferidos: number;
    readonly corrigidos: number;
    readonly novaConferencia: number;
  };
}

export const lerInvestigacao = (id: string) =>
  ler<Investigacao>(`/api/auditoria/casos/${id}/investigacao`);
export const lerResponsaveis = () => ler<Responsaveis>('/api/auditoria/responsaveis');
export const salvarResponsaveis = (
  s: Sessao,
  d: { prateleira: readonly string[]; estoque: readonly string[]; motivo: string },
) => escrever<{ versao: number }>(s, 'PUT', '/api/auditoria/responsaveis', d);
export const lerPainelDasDivergencias = (dias = 30) =>
  ler<PainelDasDivergencias>(`/api/auditoria/painel?dias=${String(dias)}`);
export const naoConsegui = (
  s: Sessao,
  id: string,
  d: {
    chave: string;
    local: LocalDaConferencia;
    motivo: MotivoSemVerificacao;
    observacao?: string | null;
  },
) => escrever<{ repetido: boolean }>(s, 'POST', `/api/auditoria/casos/${id}/nao-verificado`, d);
export const liberarParaConferir = (
  s: Sessao,
  id: string,
  d: { chave: string; local: LocalDaConferencia },
) => escrever<{ repetido: boolean }>(s, 'POST', `/api/auditoria/casos/${id}/liberar`, d);
export const lerHosAgora = (s: Sessao, id: string) =>
  escrever<{ saldo: number; lidoEm: string }>(
    s,
    'POST',
    `/api/auditoria/casos/${id}/leitura-hos`,
    {},
  );

/* ---------- Política de qualidade (14/10): os dois indicadores e a regra dos 30% ---------- */

export interface PoliticaDeQualidade {
  readonly ativa: boolean;
  readonly limitePct: number;
  readonly minimoContados: number;
  /** Só Giro deste dia em diante pode bloquear; null = nunca ligada. */
  readonly desde: string | null;
  readonly versao: number;
  readonly motivo: string | null;
  readonly vigenteDesde: string | null;
}
export interface ContaDaQualidade {
  readonly contados: number;
  readonly errosContagem: number;
  readonly pendentesContagem: number;
  readonly tarefasReposicao: number;
  readonly falhasReposicao: number;
  readonly pendentesReposicao: number;
}
export interface DiaDaQualidade {
  readonly dia: string;
  readonly conta: ContaDaQualidade;
  readonly avaliacao: {
    /** O que bloqueia (dono 14/10): falhas de reposição ÷ mandados repor. */
    readonly reposicao: {
      readonly taxa: number;
      readonly taxaMaxima: number;
      readonly nivel: string;
      readonly bloqueia: boolean;
    };
    /** Só acompanhamento. */
    readonly contagem: { readonly taxa: number; readonly taxaMaxima: number };
    readonly bloqueioPrevisto: boolean;
  };
  readonly bloqueio: {
    readonly id: string;
    readonly origem: string;
    readonly dia: string;
    readonly liberado: boolean;
    readonly liberadoMotivo: string | null;
  } | null;
}
export interface QualidadeDaPessoa {
  readonly accountId: string;
  readonly nome: string;
  readonly total: ContaDaQualidade;
  readonly taxaContagem: number;
  readonly taxaReposicao: number;
  readonly diasComBloqueioPrevisto: number;
  readonly dias: readonly DiaDaQualidade[];
}
export interface QualidadeDaEquipe {
  readonly de: string;
  readonly ate: string;
  readonly politica: PoliticaDeQualidade;
  readonly pessoas: readonly QualidadeDaPessoa[];
}

export const lerQualidadeDaEquipe = (de: string, ate: string, pessoa?: string | null) =>
  ler<QualidadeDaEquipe>(
    `/api/auditoria/qualidade-equipe?${new URLSearchParams({
      de,
      ate,
      ...(pessoa ? { pessoa } : {}),
    }).toString()}`,
  );
export const salvarPoliticaDeQualidade = (
  s: Sessao,
  d: { ativa: boolean; limitePct: number; minimoContados: number; motivo: string },
) =>
  escrever<{ versao: number; desde: string | null }>(
    s,
    'PUT',
    '/api/auditoria/politica-qualidade',
    d,
  );
